import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Check, AlertCircle, Loader2, ArrowLeft, CreditCard,
} from 'lucide-react';

import { getReserva, cancelarReserva, crearCheckoutSession, ApiError } from '../lib/api';
import { formateaCodigoReserva, formateaFechaHora } from '../lib/fechas';
import { etiquetaSala } from '../lib/sala';

const RX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatEuros(n) {
  if (n == null) return '';
  const num = Number(n);
  if (Number.isInteger(num)) return `${num} €`;
  return `${num.toFixed(2).replace('.', ',')} €`;
}

export default function Reserva() {
  const { codigo } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();

  const stripeStatus = searchParams.get('stripe'); // 'success' | 'cancelled' | null

  // Si volvemos de Stripe checkout, leemos el email cacheado en sessionStorage
  // (lo guardamos al lanzar el checkout en el modal). Evita re-tipear.
  const sessionKey = `idy_reserva_email_${codigo}`;
  const cachedEmail = (() => {
    try { return sessionStorage.getItem(sessionKey) || ''; } catch { return ''; }
  })();
  const [email, setEmail] = useState(cachedEmail);
  const [emailConfirmado, setEmailConfirmado] = useState(cachedEmail || null);
  const [errorEmail, setErrorEmail] = useState(null);
  const [reserva, setReserva] = useState(null);
  const [estado, setEstado] = useState('idle'); // 'idle' | 'consultando' | 'ok' | 'error' | 'cancelando' | 'cancelado'
  const [errorApi, setErrorApi] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [reintentandoPago, setReintentandoPago] = useState(false);

  // Consulta cuando hay email confirmado
  useEffect(() => {
    if (!emailConfirmado) return;
    let cancelled = false;
    setEstado('consultando');
    setErrorApi(null);
    getReserva(codigo, emailConfirmado)
      .then((data) => {
        if (cancelled) return;
        setReserva(data);
        setEstado('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        setReserva(null);
        setEstado('error');
        if (err instanceof ApiError) {
          setErrorApi({ status: err.status, code: err.code, msg: err.message });
        } else {
          setErrorApi({ status: 0, code: 'network', msg: err.message });
        }
      });
    return () => { cancelled = true; };
  }, [codigo, emailConfirmado]);

  const submitEmail = (e) => {
    e.preventDefault();
    setErrorEmail(null);
    if (!RX_EMAIL.test(email)) {
      setErrorEmail(t('modal.errores.email_invalido'));
      return;
    }
    setEmailConfirmado(email.trim());
  };

  const reintentarPago = async () => {
    if (!emailConfirmado) return;
    setReintentandoPago(true);
    setErrorApi(null);
    try {
      const session = await crearCheckoutSession(codigo, emailConfirmado);
      if (session?.url) {
        window.location.href = session.url;
        return;
      }
      throw new Error('Sin URL de checkout');
    } catch (err) {
      setReintentandoPago(false);
      if (err instanceof ApiError) {
        setErrorApi({ status: err.status, code: err.code, msg: err.message });
      } else {
        setErrorApi({ status: 0, code: 'network', msg: err.message });
      }
    }
  };

  const cerrarBannerStripe = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('stripe');
    setSearchParams(next, { replace: true });
  };

  const cancelar = async () => {
    setConfirmando(false);
    setEstado('cancelando');
    setErrorApi(null);
    try {
      const resp = await cancelarReserva(codigo, emailConfirmado, lang);
      setReserva((r) => ({ ...r, estado: 'cancelada', cancelada_at: resp.cancelada_at, reembolso_pendiente: resp.reembolso_pendiente }));
      setEstado('cancelado');
    } catch (err) {
      setEstado('ok'); // volver al detalle
      if (err instanceof ApiError) {
        setErrorApi({ status: err.status, code: err.code, msg: err.message });
      } else {
        setErrorApi({ status: 0, code: 'network', msg: err.message });
      }
    }
  };

  // -------- Render -------
  return (
    <main className="reserva-page">
      <Link to="/horarios" className="reserva-volver">
        <ArrowLeft size={16} strokeWidth={1.5} /> {t('reserva_detail.btn_volver')}
      </Link>

      <header className="reserva-head">
        <span className="section-eyebrow">{t('reservas.eyebrow')}</span>
        <h1 className="reserva-titulo">{t('reserva_detail.titulo')}</h1>
      </header>

      {/* Banner Stripe — sólo si vienes del checkout */}
      {stripeStatus === 'success' && (
        <div className="banner banner-success">
          <Check size={18} strokeWidth={2} />
          <span>{t('reserva_detail.stripe_success')}</span>
          <button type="button" className="banner-cerrar" onClick={cerrarBannerStripe} aria-label={t('comun.cerrar')}>×</button>
        </div>
      )}
      {stripeStatus === 'cancelled' && (
        <div className="banner banner-warn">
          <AlertCircle size={18} strokeWidth={2} />
          <div className="banner-cuerpo">
            <span>{t('reserva_detail.stripe_cancelled')}</span>
            {emailConfirmado && (
              <button
                type="button"
                className="banner-cta"
                onClick={reintentarPago}
                disabled={reintentandoPago}
              >
                {reintentandoPago ? (
                  <><Loader2 size={14} className="spin" strokeWidth={1.5} /> …</>
                ) : (
                  <><CreditCard size={14} strokeWidth={1.5} /> {t('reserva_detail.stripe_reintentar')}</>
                )}
              </button>
            )}
          </div>
          <button type="button" className="banner-cerrar" onClick={cerrarBannerStripe} aria-label={t('comun.cerrar')}>×</button>
        </div>
      )}

      {/* Paso 1 — pedir email */}
      {!emailConfirmado && (
        <section className="reserva-pide-email">
          <h2 className="reserva-h2">{t('reserva_detail.pide_email_titulo')}</h2>
          <p className="reserva-sub">{t('reserva_detail.pide_email_sub')}</p>

          <div className="reserva-pide-codigo">
            {t('reserva_detail.codigo_label')}: <strong>{formateaCodigoReserva(codigo)}</strong>
          </div>

          <form className="reserva-form-email" onSubmit={submitEmail} noValidate>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('reserva_detail.email_placeholder')}
              autoFocus
              aria-invalid={!!errorEmail}
            />
            {errorEmail && <div className="modal-error-campo">{errorEmail}</div>}
            <button type="submit" className="btn-primary">
              {t('reserva_detail.btn_consultar')}
            </button>
          </form>
        </section>
      )}

      {/* Paso 2 — consultando */}
      {emailConfirmado && estado === 'consultando' && (
        <div className="reserva-loading">
          <Loader2 size={20} className="spin" strokeWidth={1.5} />
          <span>{t('comun.cargando')}…</span>
        </div>
      )}

      {/* Paso 3 — error tras consultar */}
      {emailConfirmado && estado === 'error' && errorApi && (
        <section className="reserva-no-encontrada">
          <h2 className="reserva-h2">{t('reserva_detail.no_encontrada_titulo')}</h2>
          <p className="reserva-sub">{t('reserva_detail.no_encontrada_sub')}</p>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => { setEmailConfirmado(null); setEmail(''); }}
          >
            {t('comun.reintentar')}
          </button>
        </section>
      )}

      {/* Paso 4 — datos de la reserva (estado=ok o cancelando) */}
      {reserva && (estado === 'ok' || estado === 'cancelando') && (
        <ReservaDetalle
          reserva={reserva}
          lang={lang}
          errorApi={errorApi}
          enviando={estado === 'cancelando'}
          onCancelar={() => setConfirmando(true)}
        />
      )}

      {/* Paso 5 — cancelación confirmada */}
      {estado === 'cancelado' && reserva && (
        <ReservaCancelada reserva={reserva} lang={lang} />
      )}

      {/* Diálogo de confirmación */}
      {confirmando && (
        <div className="modal-backdrop" onClick={() => setConfirmando(false)}>
          <div className="modal modal-confirm" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-titulo">{t('reserva_detail.confirmar_titulo')}</h2>
            <p className="modal-confirm-texto">{t('reserva_detail.confirmar_texto')}</p>
            <div className="modal-acciones">
              <button type="button" className="btn-primary" onClick={cancelar}>
                {t('reserva_detail.confirmar_si')}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setConfirmando(false)}>
                {t('reserva_detail.confirmar_no')}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function ReservaDetalle({ reserva, lang, errorApi, enviando, onCancelar }) {
  const { t } = useTranslation();
  const codigo = formateaCodigoReserva(reserva.codigo_reserva);
  const cancelable = reserva.cancelable && !enviando;

  return (
    <section className="reserva-detalle">
      <div className="reserva-bloque">
        <div className="reserva-bloque-titulo">{reserva.clase_tipo?.nombre}</div>
        <div className="reserva-bloque-linea">
          {formateaFechaHora(reserva.fecha_clase, reserva.hora_inicio, lang)}
        </div>
        {reserva.profesor && (
          <div className="reserva-bloque-linea reserva-bloque-sub">
            {t('reservas.con_profesor', { nombre: reserva.profesor.nombre_publico })}
          </div>
        )}
        <div className="reserva-bloque-linea reserva-bloque-sub">
          {t('reservas.formato_label.' + reserva.formato)}
          {reserva.sala && <> · {etiquetaSala(reserva.sala, t)}</>}
        </div>
      </div>

      <div className="reserva-stats">
        <div className="reserva-stat">
          <div className="reserva-stat-label">{t('reserva_detail.codigo_label')}</div>
          <div className="reserva-stat-valor reserva-stat-codigo">{codigo}</div>
        </div>
        <div className="reserva-stat">
          <div className="reserva-stat-label">{t('reserva_detail.estado_label')}</div>
          <div className={'reserva-stat-valor reserva-estado-' + reserva.estado}>
            {t('reserva_detail.estado.' + reserva.estado)}
          </div>
        </div>
        {reserva.pago && (
          <div className="reserva-stat">
            <div className="reserva-stat-label">{t('modal.importe_label')}</div>
            <div className="reserva-stat-valor">{formatEuros(reserva.pago.importe_euros)}</div>
          </div>
        )}
      </div>

      {reserva.cancelable_motivo && (
        <p className="reserva-cancelable-motivo">{reserva.cancelable_motivo}</p>
      )}

      {errorApi && errorApi.code === 'CANCELACION_FUERA_PLAZO' && (
        <div className="modal-error-general">
          <AlertCircle size={16} strokeWidth={1.75} />
          <span>{errorApi.msg}</span>
        </div>
      )}

      {cancelable && (
        <button
          type="button"
          className="btn-ghost reserva-btn-cancelar"
          onClick={onCancelar}
          disabled={enviando}
        >
          {t('reserva_detail.btn_cancelar')}
        </button>
      )}
    </section>
  );
}

function ReservaCancelada({ reserva, lang }) {
  const { t } = useTranslation();
  return (
    <section className="reserva-cancelada">
      <div className="confirmacion-icono confirmacion-icono--cancel">
        <Check size={36} strokeWidth={2} />
      </div>
      <h2 className="confirmacion-titulo">{t('reserva_detail.cancelada_ok')}</h2>
      {reserva.reembolso_pendiente && (
        <p className="reserva-sub">{t('reserva_detail.cancelada_reembolso')}</p>
      )}
      <Link to="/horarios" className="btn-primary">
        {t('reserva_detail.btn_volver')}
      </Link>
    </section>
  );
}
